import { z } from 'zod';
import {
  AreaOfStudyCategoryValues,
  AxiomEmployeeTypeValues,
  CandidateSearchFilters,
  LegalTechSkillsProficiencies,
  WorkPreferenceKeys,
} from '@axiom/const';
import { SchemaDate, ZodArrayToEnum } from '@axiom/types';

import {
  CandidateCountryCodes,
  CandidateEmployeeTypes,
  CandidateProfileStatuses,
} from './candidate';
import { LanguageProficiencies } from './language';

export const TALENT_MIN_COMPENSATION_RATE = 0;
export const TALENT_MAX_COMPENSATION_RATE = 2250000;

export const TALENT_MIN_WEEKLY_AVAILABILITY = 0;
export const TALENT_MAX_WEEKLY_AVAILABILITY = 80;

export const TALENT_MIN_YEARS_OF_EXPERIENCE = 0;
export const TALENT_MAX_YEARS_OF_EXPERIENCE = 50;

const containsInvalidCharacters = ({
  searchTerm = '',
}: {
  searchTerm?: string | null;
}) => {
  const term = searchTerm ?? '';
  const invalidChars = /([!()+/:[\\\]^{}~-])/g;
  return invalidChars.test(term);
};

export const rangeRefinementError = 'To cannot be less than From';
export const decimalPlaceError = 'Only whole numbers allowed';
export const expandFaRequiredError = 'At least one option should be selected';
export const invalidCharactersErrorMessage = String.raw`Your search contains one or more of the following invalid characters ^ : / \ ( ) { } [ ] ! - ~ +. Please update your search and try again`;

const handleUndefinedNumber = ({ val }: { val: number | undefined | null }) =>
  val || val === 0 ? Number(val) : undefined;

const isStartLessThanOrEqualToEnd = ({
  start,
  end,
}: {
  start: number | undefined | null;
  end: number | undefined | null;
}) => {
  const a = handleUndefinedNumber({ val: start });
  const b = handleUndefinedNumber({ val: end });

  return a === undefined || b === undefined || a <= b;
};

const buildRangeField = ({ min, max }: { min: number; max: number }) => {
  return z
    .number()
    .int(decimalPlaceError)
    .nonnegative()
    .min(min)
    .max(max)
    .nullish();
};

const buildRangeSchema = ({ min, max }: { min: number; max: number }) => {
  return z
    .object({
      start: buildRangeField({ min, max }),
      end: buildRangeField({ min, max }),
    })
    .nullish()
    .refine(
      data =>
        isStartLessThanOrEqualToEnd({ start: data?.start, end: data?.end }),
      {
        message: rangeRefinementError,
        path: ['end'],
      }
    );
};

export const EnvoyCandidateSearchFiltersCompensationSchema = buildRangeSchema({
  min: TALENT_MIN_COMPENSATION_RATE,
  max: TALENT_MAX_COMPENSATION_RATE,
});

export const EnvoyCandidateSearchFiltersWeeklyAvailabilitySchema =
  buildRangeSchema({
    min: TALENT_MIN_WEEKLY_AVAILABILITY,
    max: TALENT_MAX_WEEKLY_AVAILABILITY,
  });

export const EnvoyCandidateSearchFiltersYearsOfExperienceSchema =
  buildRangeSchema({
    min: TALENT_MIN_YEARS_OF_EXPERIENCE,
    max: TALENT_MAX_YEARS_OF_EXPERIENCE,
  });

export const TALENT_DEFAULT_MINIMUM_YEARS_OF_EXPERIENCE = 2;

const buildMinYearsField = () =>
  z
    .number()
    .int(decimalPlaceError)
    .min(TALENT_MIN_YEARS_OF_EXPERIENCE)
    .max(TALENT_MAX_YEARS_OF_EXPERIENCE)
    .nullish();

export const EnvoyCandidateSearchFiltersSchema = z.object({
  addressCountryCode: z.enum(ZodArrayToEnum(CandidateCountryCodes)).nullish(),
  addressStates: z.array(z.string()).nullish(),
  areaOfStudyCategories: z
    .array(z.enum(ZodArrayToEnum(AreaOfStudyCategoryValues)))
    .nullish(),
  axiomEmployeeType: z
    .array(z.enum(ZodArrayToEnum(AxiomEmployeeTypeValues)))
    .nullish(),
  barredLocations: z.array(z.string().uuid()).nullish(),
  compensation: EnvoyCandidateSearchFiltersCompensationSchema,
  daysToSoonestEngagementEnd: z.number().nullish(),
  educationInstitution: z
    .string()
    .nullish()
    .refine(s => !containsInvalidCharacters({ searchTerm: s }), {
      message: invalidCharactersErrorMessage,
    }),
  employeeType: z
    .array(z.enum(ZodArrayToEnum(CandidateEmployeeTypes)))
    .nullish(),
  experienceAtClient: z
    .string()
    .nullish()
    .refine(s => !containsInvalidCharacters({ searchTerm: s }), {
      message: invalidCharactersErrorMessage,
    }),
  excludeCrossFulfilled: z.boolean().default(false).nullish(),
  expandFa: z
    .array(
      z.enum(ZodArrayToEnum(Object.values(CandidateSearchFilters.expandFa)))
    )
    .min(1, { message: expandFaRequiredError }),
  generalSkills: z.array(z.string()).nullish(),
  industries: z.array(z.string()).nullish(),
  industriesMinYears: buildMinYearsField(),
  isPriorityTalent: z.boolean().nullish(),
  isProfileShared: z.boolean().nullish(),
  languages: z.array(z.string().uuid()).nullish(),
  languageProficiencies: z
    .array(z.enum(ZodArrayToEnum(LanguageProficiencies)))
    .nullish(),
  legalSkills: z.array(z.string()).nullish(),
  legalSkillsMinYears: buildMinYearsField(),
  legalTechnicalSkills: z.array(z.string()).nullish(),
  legalTechnicalSkillProficiencies: z
    .array(z.enum(ZodArrayToEnum(LegalTechSkillsProficiencies)))
    .nullish(),
  businessTitles: z.array(z.string()).nullish(),
  ownerUserId: z.array(z.string().uuid()).nullish(),
  practiceAreasAndFocusAreas: z
    .object({
      practiceAreas: z.array(z.string()).nullish(),
      focusAreas: z.array(z.string()).nullish(),
    })
    .nullish(),
  practiceAreasMinYears: buildMinYearsField(),
  profileStatus: z
    .array(z.enum(ZodArrayToEnum(CandidateProfileStatuses)))
    .nullish(),
  search: z
    .string()
    .nullish()
    .refine(s => !containsInvalidCharacters({ searchTerm: s }), {
      message: invalidCharactersErrorMessage,
    }),
  tags: z.array(z.string().uuid()).nullish(),
  weeklyAvailability: EnvoyCandidateSearchFiltersWeeklyAvailabilitySchema,
  workPreferences: z
    .array(
      z.enum(
        ZodArrayToEnum(
          Object.keys(WorkPreferenceKeys) as Array<
            keyof typeof WorkPreferenceKeys
          >
        )
      )
    )
    .nullish(),
  yearsOfExperience: EnvoyCandidateSearchFiltersYearsOfExperienceSchema,
});

export type EnvoyCandidateSearchFilters = z.infer<
  typeof EnvoyCandidateSearchFiltersSchema
>;

export const EnvoyCandidateSearchFiltersFormSchema =
  EnvoyCandidateSearchFiltersSchema.pick({
    addressCountryCode: true,
    addressStates: true,
    areaOfStudyCategories: true,
    barredLocations: true,
    compensation: true,
    daysToSoonestEngagementEnd: true,
    educationInstitution: true,
    employeeType: true,
    experienceAtClient: true,
    excludeCrossFulfilled: true,
    generalSkills: true,
    industries: true,
    industriesMinYears: true,
    isPriorityTalent: true,
    isProfileShared: true,
    languages: true,
    languageProficiencies: true,
    legalSkills: true,
    legalSkillsMinYears: true,
    legalTechnicalSkills: true,
    legalTechnicalSkillProficiencies: true,
    businessTitles: true,
    ownerUserId: true,
    practiceAreasAndFocusAreas: true,
    practiceAreasMinYears: true,
    profileStatus: true,
    search: true,
    tags: true,
    weeklyAvailability: true,
    workPreferences: true,
    yearsOfExperience: true,
  }).extend({
    rollOffDateDropdown: z.number().nullish(),
    rollOffDatePicker: SchemaDate.nullish(),
    practiceAreasAndFocusAreasDropdown: z.array(z.string()).nullish(),
    expandFa: z
      .array(
        z.enum(ZodArrayToEnum(Object.values(CandidateSearchFilters.expandFa)))
      )
      .min(1, { message: expandFaRequiredError }),
  });

export type EnvoyCandidateSearchFiltersForm = z.infer<
  typeof EnvoyCandidateSearchFiltersFormSchema
>;

export const EnvoyCandidateSearchFiltersFormSchemaFields =
  EnvoyCandidateSearchFiltersFormSchema.keyof().Enum;
